import { ActionTypes } from "@/actions";
import { itemDelete, itemDeleteBulk, itemUpdate } from "@/app/api/itemData";
// Removed direct database imports - using API instead
import { all, call, put, select, takeEvery } from "redux-saga/effects";
import { dataAPI } from "@/app/api/dataApi";
import { invalidateItemsCacheForSlug } from "@/app/api/itemsApi";
import { normalizeOrgSegment } from "@/app/utils/collectionPaths";
import { formatErrorForDisplay } from "@/utils/errorMessage";

function organizationForSlug(
    tableMeta: unknown,
    slug: string | undefined,
    explicit?: string | null
): string | undefined {
    if (explicit != null && String(explicit).trim()) {
        return String(explicit).trim();
    }
    if (!slug || !tableMeta) return undefined;
    const arr = Array.isArray(tableMeta) ? tableMeta : [tableMeta];
    const matches = arr.filter((x: any) => x && x.slug === slug);
    const m = matches.length === 1 ? matches[0] : arr.find((x: any) => x && x.slug === slug);
    const o = m?.organization;
    return typeof o === "string" && o.trim() ? o.trim() : undefined;
}

const workerItemNew = function* () {
    try {
    } catch (error: any) {
        console.error('workerItemFieldNew', error);
    }
}

const workerItemStart = function* () {
    try {
    } catch (error: any) {
        console.error('workerItemStart', error);
    }
}

const workerItemDelete = function* (action: any): Generator<any, void, any> {
    try {
        console.log('workerItemDelete', action.payload);
        // Use slug if available, fallback to collectionName for backward compatibility
        // itemDelete will use slug from itemData, fallback to collectionName
        const tableMetaDel = yield select(selectTableMeta);
        const orgDel = organizationForSlug(tableMetaDel, action.payload.slug, action.payload.organization);
        const response = yield call(itemDelete, {
            slug: action.payload.slug,
            collectionName: action.payload.collectionName,
            uid: action.payload.uid,
            organization: orgDel
        });
        yield put({ type: ActionTypes.DELETE_ITEM_SUCCESS, payload: response.data });
    } catch (error: any) {
        console.error('workerItemDelete', error);
    }
}

const workerItemDeleteBulk = function* (action: any): Generator<any, void, any> {
    try {
        const { slug, collectionName, uids } = action.payload;
        if (!Array.isArray(uids) || uids.length === 0) {
            yield put({ type: ActionTypes.DELETE_ITEMS_BULK_SUCCESS, payload: { deletedCount: 0, deletedIds: [] } });
            return;
        }
        const tableMetaBulk = yield select(selectTableMeta);
        const orgBulk = organizationForSlug(tableMetaBulk, slug, action.payload.organization);
        const response = yield call(itemDeleteBulk, { slug, collectionName, uids, organization: orgBulk });
        yield put({ type: ActionTypes.DELETE_ITEMS_BULK_SUCCESS, payload: response });
    } catch (error: any) {
        console.error('workerItemDeleteBulk', error);
        yield put({ type: ActionTypes.DELETE_ITEMS_BULK_ERROR, payload: error?.message });
    }
}

const workerQuickAddUpdate = function* (action: any): Generator<any, void, any> {
    try {
        let updated = { ...action.payload.item };
        const user = yield select((state: any) => state.user);

        const quickSetValues = action.payload?.quickSetValues
        if (quickSetValues && typeof quickSetValues === 'object') {
            Object.entries(quickSetValues).forEach(([key, value]) => {
                updated[key] = value
            })
        }

        // Use slug for API calls - backend resolves slug to appropriate collection
        // Prefer slug from meta, fallback to collection for backward compatibility
        const slugToUse = action.payload.meta?.slug || action.payload.meta?.collection;
        const fields = action.payload.meta?.fields || [];

        // If this existing item only has uid and is missing objectId/_id,
        // try to fetch the full item once so we can preserve its database identifier.
        const hasIdentifier =
            updated.objectId ||
            updated._id ||
            updated._objectId ||
            (updated as any).object_id ||
            (updated as any)._object_id;

        if (!hasIdentifier && updated.uid && slugToUse) {
            try {
                const fullItemResponse: any = yield call([dataAPI, dataAPI.getCollectionItem], slugToUse, updated.uid);
                const fullItem = fullItemResponse?.data || fullItemResponse?.item;
                if (fullItem && typeof fullItem === 'object') {
                    const apiIdentifier =
                        fullItem.objectId ||
                        fullItem._id ||
                        fullItem._objectId ||
                        (fullItem as any).object_id ||
                        (fullItem as any)._object_id;

                    if (apiIdentifier) {
                        updated = {
                            ...updated,
                            objectId: updated.objectId || apiIdentifier,
                            _id: updated._id || fullItem._id || apiIdentifier,
                        };
                    }
                }
            } catch (lookupError) {
                console.error('workerQuickAddUpdate: Failed to resolve objectId/_id for quick update item', lookupError);
            }
        }

        const response = yield call(itemUpdate, updated, slugToUse, fields, user);
        const responseData = response?.data ?? response?.item ?? updated;
        yield put({ type: ActionTypes.QUICK_ADD_UPDATE_SUCCESS, payload: responseData });

    }
    catch (error: any) {
        console.error('workerQuickAddUpdate', error);
    }
}

const workerItemUpload = function* (action: any): Generator<any, void, any> {
    try {
        //yield put({ type: ActionTypes.EDIT_ITEM_UPDATED, payload: action.payload.filename });
        const filename = String(action.payload?.value || action.payload?.file?.filename || "").trim();
        const relation = action.payload?.relation;
        if (!filename || !relation) {
            return;
        }
        yield put({
            type: ActionTypes.FILE_NEW_WITH_RELATION,
            payload: {
                filename,
                relation,
                userId: action.payload.userId,
            }
        });
    } catch (error: any) {
        console.error('workerItemUpload', error);
    }
}

const selectCurrentItem = (state: any) => state.item;
const selectTableMeta = (state: any) => state.table.meta;
const handleUpdateAndStore = function* (action: any): Generator<any, void, any> {
    try {
        const key = typeof action.payload?.key === 'string' ? action.payload.key.trim() : '';
        if (!key) {
            console.warn('handleUpdateAndStore: missing update key', action.payload);
            return;
        }
        const currentItem = yield select(selectCurrentItem);
        const user = yield select((state: any) => state.user);
        const skipItemReduxPatch = action.payload.skipItemReduxPatch === true;
        const payloadItem = action.payload.item;
        const baseFromPayload =
            payloadItem &&
                typeof payloadItem === 'object' &&
                !Array.isArray(payloadItem)
                ? { ...payloadItem }
                : null;
        const updatedItemData = {
            ...(baseFromPayload ?? (currentItem.current && typeof currentItem.current === 'object' ? { ...currentItem.current } : {})),
            [key]: action.payload.value,
            _updated: new Date().getTime()
        };

        if (!skipItemReduxPatch) {
            yield put({
                type: ActionTypes.EDIT_ITEM_UPDATED,
                payload: { key, value: action.payload.value }
            });
        }

        // Use slug for API calls - backend resolves slug to appropriate collection
        // Prefer slug if available, fallback to collectionName for backward compatibility
        const slugToUse = action.payload.slug || action.payload.collectionName;
        const tableMetaList = yield select(selectTableMeta);
        const tableCtxKey: string | undefined =
            action.payload.tableKey || (yield select((s: any) => s.table.currentSlug));

        // Get field definitions from meta (from action payload or Redux state)
        let fields: any[] = [];
        if (action.payload.meta?.fields) {
            fields = action.payload.meta.fields;
        } else {
            if (Array.isArray(tableMetaList)) {
                let meta = tableCtxKey ? tableMetaList.find((m: any) => m?.tableKey === tableCtxKey) : undefined;
                if (!meta) {
                    meta = tableMetaList.find((m: any) => m?.slug === slugToUse);
                }
                if (meta?.fields) {
                    fields = meta.fields;
                }
            } else if (tableMetaList?.fields) {
                fields = tableMetaList.fields;
            }
        }

        // For user actions and other "quick" updates, we can safely use the
        // dedicated quick-update endpoint which accepts uid as identifier.
        const useQuickUpdate = action.payload.useQuickUpdate === true;
        let response: any;
        if (useQuickUpdate && updatedItemData.uid && slugToUse) {
            const quickPayload = { field: key, value: action.payload.value };
            response = yield call([dataAPI, dataAPI.quickUpdateCollectionItem], slugToUse, updatedItemData.uid, quickPayload);
        } else {
            response = yield call(
                itemUpdate,
                updatedItemData,
                slugToUse,
                fields,
                user,
                { isNew: baseFromPayload?.uid ? false : currentItem.isNew }
            );
        }

        const responseData = response?.data ?? response?.item ?? updatedItemData;
        if (!skipItemReduxPatch) {
            yield put({ type: ActionTypes.STORE_UPDATED_SUCCESS, payload: responseData });
        }

        if (slugToUse) {
            let metaRow: any;
            if (Array.isArray(tableMetaList)) {
                metaRow = tableCtxKey ? tableMetaList.find((m: any) => m?.tableKey === tableCtxKey) : undefined;
                if (!metaRow) {
                    metaRow = tableMetaList.find((m: any) => m?.slug === slugToUse);
                }
            }
            const orgNorm = metaRow?.organization
                ? normalizeOrgSegment(metaRow.organization)
                : action.payload.organization
                    ? normalizeOrgSegment(action.payload.organization)
                    : undefined;
            invalidateItemsCacheForSlug(slugToUse, orgNorm);
            const listFilters = action.payload?.listFilters;
            yield put({
                type: ActionTypes.TABLE_REQUEST,
                payload: {
                    slugName: slugToUse,
                    tableKey: metaRow?.tableKey || tableCtxKey,
                    organization: orgNorm || undefined,
                    user,
                    ...(listFilters && Object.keys(listFilters).length > 0 ? { filters: listFilters } : {}),
                }
            });
        }
    } catch (error: any) {
        console.error('handleUpdateAndStore', error);
        const slugToUseCatch =
            (typeof action.payload?.slug === 'string' && action.payload.slug.trim()) ||
            (typeof action.payload?.collectionName === 'string' && action.payload.collectionName.trim()) ||
            undefined;
        let tableCtxKeyCatch: string | undefined =
            typeof action.payload?.tableKey === 'string' ? action.payload.tableKey.trim() : undefined;
        if (!tableCtxKeyCatch) {
            tableCtxKeyCatch = yield select((s: any) => s.table.currentSlug);
        }
        const metaList = yield select(selectTableMeta);
        let metaRow: any;
        if (Array.isArray(metaList) && slugToUseCatch) {
            metaRow = tableCtxKeyCatch
                ? metaList.find((m: any) => m?.tableKey === tableCtxKeyCatch)
                : undefined;
            if (!metaRow) {
                metaRow = metaList.find((m: any) => m?.slug === slugToUseCatch);
            }
        }
        yield put({
            type: ActionTypes.TABLE_FAILURE,
            payload: {
                message: formatErrorForDisplay(error),
                type: 'ITEM_UPDATE_ERROR',
                tableKey: metaRow?.tableKey || tableCtxKeyCatch,
                slug: slugToUseCatch,
            },
        });
    }
}


export function* watcherItemSaga() {
    yield all([
        takeEvery(ActionTypes.EDIT_ITEM_UPDATE_AND_STORE, handleUpdateAndStore),
        takeEvery(ActionTypes.EDIT_ITEM_NEW, workerItemNew),
        takeEvery(ActionTypes.EDIT_ITEM_START, workerItemStart),
        takeEvery(ActionTypes.EDIT_ITEM_UPLOAD, workerItemUpload),
        takeEvery(ActionTypes.QUICK_ADD_UPDATE, workerQuickAddUpdate),
        takeEvery(ActionTypes.DELETE_ITEM, workerItemDelete),
        takeEvery(ActionTypes.DELETE_ITEMS_BULK, workerItemDeleteBulk),
    ])
}
